import React, {ReactElement, useMemo, useEffect, useContext} from 'react';
import Head from 'next/head';
import styled from 'styled-components';

import MainLayout from 'components/layout/mainLayout';
import PageWrapper from 'components/pageWrapper';
import Icon from 'components/icons/icon';
import LoaderCover from 'components/loaderCover';
import Pagination from 'components/pagination';

import SearchSmallToolbar from 'components/pages/search/searchSmallToolbar';
import SearchLargeToolbar from 'components/pages/search/searchLargeToolbar';
import SearchSidebar from 'components/pages/search/searchSidebar';
import SearchCard from 'components/pages/search/searchCard';
import SearchShowroomCard from 'components/pages/search/searchShowroomCard';

import seo from 'utils/seo';

import theme, {bp, color} from 'settings/theme';

import {SearchContext} from 'services/searchService';

type Props = {
  pageData: any,
  catData: any,
  sideBarIsOpen: boolean,
  toggleSidebar: () => void
};

type StyledProps = {
  sideBarIsOpen: boolean
};


// Product Search Page
const Page: NextPageExtended<Props> = (props) => {

  // Get content from supplied StoryBlok story
  const content = useMemo(() => {
    return props.pageData?.content || {};
  }, [props.pageData]);

  const seoContent = content?.seo;

  const search = useContext(SearchContext);
  
  // Create category header
  const header = useMemo(() => {

      let header;
      
      if(props.catData){
        header = {
          title: props.catData.content?.name,
          intro: props.catData.content?.intro
        };
      }
      else{
        header = {
          title: props.pageData.content?.title,
          intro: props.pageData.content?.intro
        };
    }

      return header;

  }, [props.pageData, props.catData]);


  return (

    <>
    
      <Head>
        <title>{seo.createTitle(seoContent?.title)}</title>
        <meta name="description" content={seoContent?.description} />
      </Head>

      <StyledPage sideBarIsOpen={props.sideBarIsOpen} className="searchPage">

        <SearchSmallToolbar toggleSidebar={props.toggleSidebar} />

        <PageWrapper>

          <div className="searchPage-layout">

            {/* Sidebar  */}
            <div className="searchPage-layout-sidebar">
              <SearchSidebar toggleSidebar={props.toggleSidebar} />
            </div>

            <div className="searchPage-layout-main">
                
              {/* Intro  */}
              <div className="searchPage-intro">
                  
                <h1>{header.title}</h1>

                <div className="searchPage-intro-text">
                  {header.intro}
                </div>

              </div>


              {/* Large Toolbar  */}
              <SearchLargeToolbar />


              {/* Results  */}
              <div className="searchPage-resultsPanel">

                {search.isLoading &&
                  <LoaderCover height="200px" />
                }

                {!search.isLoading && search.results && search.results.length > 0 && 
                  <div className="searchPage-results">

                    {search.results.map((product, index) => (
                      <>
                        {/* {index === 2 && (
                            <div className="searchPage-insertedCard">
                              <SearchShowroomCard />
                            </div>
                        )} */}
                        <SearchCard product={product} key={product.id} />
                      </>
                    ))}

                    <div className="searchPage-bottomPagination">
                      <Pagination 
                        totalCount={search.meta?.totalHits ?? 0} 
                        itemsPerPage={search.meta?.hitsPerPage}
                        currentPage={search.meta?.page ?? 1}
                        pagesToShow={5}
                        showDirectionButtons={true}
                        showLimitButtons={true}
                        urlMode="query"
                      />
                    </div>

                  </div>
                }

              </div>

            </div>

          </div>

        </PageWrapper>

        <div className="searchPage-drawer">

          <div className="searchPage-drawer-background" onClick={props.toggleSidebar}></div>

          <div className="searchPage-drawer-panel">
            <button className="searchPage-drawer-close" onClick={props.toggleSidebar}>
              <div className="searchPage-drawer-close-text">CLOSE</div>
              <Icon iconName="chevron" iconDirection="right" />
            </button>
            <SearchSidebar toggleSidebar={props.toggleSidebar} />
          </div>

        </div>

      </StyledPage>
  
    </>
  
  );

}



Page.getLayout = (page: ReactElement) => {
  return (
    <MainLayout masterTemplateData={page.props.masterTemplateData}>
      {page}
    </MainLayout>
  );
}

export default Page




// Styles
const StyledPage = styled.div<StyledProps>`

  .searchPage-layout{

    display: flex;
    align-items: stretch;
    min-height: 100%;
    width: 100%;

    .searchPage-layout-sidebar{
      display: none;
    }

    .searchPage-layout-main{
      flex: 1 1 auto;
      position: relative;
      z-index: 0;
    }

  }

  .searchPage-drawer{

    .searchPage-drawer-background{
      z-index: 2;
      position: fixed;
      top: 0px;
      right: 0px;
      bottom: 0px;
      left: 0px;
      background-color: ${props => props.sideBarIsOpen ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0)'};
      pointer-events: ${props => props.sideBarIsOpen ? 'all' : 'none'};
      transition: background-color 0.15s ease-out;
    }

    .searchPage-drawer-panel{

      transition: transform 0.15s ease-out;
      transform: ${props => props.sideBarIsOpen ? 'translate(0px)' : 'translate(300px)'};

      z-index: 3;
      position: fixed;
      top: 0px;
      right: 0px;
      bottom: 0px;

      display: flex;
      flex-direction: column;

      width: 280px;
      border-left: solid 1px ${color.lines};
      box-shadow: -2px 0 6px rgba(0,0,0,0.15);
      background-color: white;

    }

    .searchPage-drawer-close{

      z-index: 1;
      flex: 0 0 auto;
      display: flex;
      align-items: center;
      justify-content: flex-end;
      width: 100%;
      height: 50px;
      padding-right: 12px;
      color: ${color.grey(20)};
      box-shadow: 0 2px 4px rgba(0,0,0,0.1);
      background-color: white;

      .searchPage-drawer-close-text{
        font-family: ${theme.typography.fontFamilySecondary};
        font-size: 14px;
        font-weight: bold;
        transition: all 0.15s ease-out;
      }

      .icon{

        width: 11px;
        height: 11px;
        margin-left: 8px;
        transition: all 0.15s ease-out;

        svg{
          fill: ${color.grey(20)};
        }

      }

      &:hover, &:focus{
        .searchPage-drawer-close-text, .icon{opacity: 0.75;}
      }

    }

      
  }


  .searchPage-intro{

    padding: 2rem 24px;
    font-size: 14px;
    border-bottom: solid 1px ${color.lines};
    background-color: white;

    h1{
      margin-bottom: 0.6rem;
      font-size: 18px;
      color: ${color.grey(10)};
    }
      
  }

  .searchPage-resultsPanel{
    padding-top: 2rem;
    padding-bottom: 2rem;
  }

  .searchPage-results{

    display: flex;
    flex-direction: column;

    > *{
      &:not(:last-child){
        margin-bottom: 20px;
      }
    }

  }

  .searchPage-bottomPagination{
    display: flex;
    justify-content: center;
    width: 100%;
    margin-top: 1rem;
    margin-bottom: 1rem;
  }


  @media (min-width: ${bp(3)}) {

    .paddingHorizontalSearch{
      padding-left: 30px;
      padding-right: 30px;
    }

    .searchPage-intro{

      padding: 2rem 30px;

      h1{
        font-size: 22px;
      }

    }

    .searchPage-resultsPanel{
      padding-left: 30px;
      padding-right: 30px;
      padding-bottom: 200px;
    }

  }

  
  @media (min-width: ${bp(4)}) {

    .searchPage-layout{

      .searchPage-layout-sidebar{
        display: flex;
        flex: 0 0 280px;
        border-right: solid 1px ${color.lines};
        background-color: white;
      }

    }

    .searchPage-drawer{

      .searchPage-drawer-background{
        display: none;
      }

      .searchPage-drawer-panel{
        display: none;
      }
        
    }

    .searchPage-intro{

      font-size: 15px;

      h1{
        font-size: 24px;
      }

    }

    

    .searchPage-results{

      > *{
        &:not(:last-child){
          margin-bottom: 30px;
        }
      }

    }

  }


  @media (min-width: ${bp(5)}) {

    .searchPage-layout{

      .searchPage-layout-sidebar{
        flex: 0 0 320px;
      }

    }

    .searchPage-intro{
      padding: 3rem 50px;
    }

    .searchPage-resultsPanel{
      padding-left: 50px;
      padding-right: 50px;
    }

  }



`;



